import { ActivityInfo } from '@/modules/drivers/types/activities'
import React, { useEffect } from 'react'
import { formatDriverName } from '@/modules/drivers/utils/driverName'
import Timeline from '../../../components_new/Timeline'
import { TimelineProps } from '../../../components_new/timelineProps'
import { SCENE_HEIGHT } from '../../../components_new/utils/layout'
import moment from 'moment'
import { ExistingDelegationModel } from '@/app/models/existingDelegationModel'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { LoaderIcon } from '@/app/features/icons/Loader'
import useDriverActivityService from '@/modules/drivers/services/DriverActivityService'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { addDays } from 'date-fns'
import { DateField } from '@/ui/form/controls/DateField'
import { FieldsetMultiSourceSelect } from '@/modules/settings/components/FieldsetMultiSourceSelect'
import useVehicleService from '@/modules/drivers/services/VehicleService'
import {
  FaIcon,
  faChevronRight,
  faChevronLeft,
  faChevronsLeftSolid,
  faChevronsRightSolid,
} from '@/lib/fa'
import { fa1, fa2 } from '@fortawesome/free-solid-svg-icons'
import DividerVertical from '@/modules/drivers/shared/DividerVertical'
import ChevronsTripleLeft from '@/assets/icons_new/ChervronsTripleLeft.svg'
import Tooltip from '@/modules/drivers/shared/ToolTip'

const CompareChart = ({
  setPassedDays,
  passedDays,
  companyId,
  drivers,
  isLoading,
  vehicles,
  onDataLoaded,
  onDateChange,
  suggestedDriverId,
  suggestedDate,
}: {
  setPassedDays: (days: number) => void
  passedDays: number
  companyId: string
  drivers: any[]
  isLoading: boolean
  vehicles: any[]

  onDataLoaded?: (
    data: ActivityInfo | null,
    selectedDate: string | null,
  ) => void

  onDateChange?: (date: string | null) => void

  suggestedDriverId?: number | null
  suggestedDate?: string | null
}) => {
  const { t } = useLocalize()
  const { getVehicleActivity } = useVehicleService()
  const { getDriverActivity } = useDriverActivityService()
  const { formatApiDate } = useDate()
  const [selectedDriver, setSelectedDriver] = React.useState(null)
  const [selectedVehicle, setSelectedVehicle] = React.useState(null)
  const [activeSourceId, setActiveSourceId] = React.useState('drivers')
  const [selectedDate, setSelectedDate] = React.useState(null)
  const [fetchingActivity, setFetchingActivity] = React.useState(false)
  const [data, setData] = React.useState<ActivityInfo | null>(null)

  const wrapperRef = React.useRef(null)
  const [chartWidth, setChartWidth] = React.useState(0)
  const [hoveredActivity, setHoveredActivity] = React.useState(null)

  const currentDate = moment().format('YYYY-MM-DD')
  const formatedDate = moment(selectedDate || currentDate)
  const [selectedSlot, setSelectedSlot] = React.useState('1')
  const activities = data?.activities || []

  const isLoadingData = isLoading || fetchingActivity
  const isDisabled = isLoading || (!drivers?.length && !vehicles?.length)
  const selectedEntity = selectedDriver || selectedVehicle

  const getDailyActivity = async () => {
    if (!selectedEntity) return

    setFetchingActivity(true)
    try {
      const dateObj = new Date(selectedDate)
      let result: ActivityInfo | null = null
      if (selectedDriver) {
        result = await getDriverActivity({
          companyId,
          driverId: selectedEntity.id,
          startTime: formatApiDate(dateObj),
          endTime: formatApiDate(addDays(dateObj, 1)),
          includeTrips: true,
        })
      } else if (selectedVehicle) {
        result = await getVehicleActivity({
          companyId,
          vehicleId: selectedEntity.id,
          startTime: formatApiDate(dateObj),
          endTime: formatApiDate(addDays(dateObj, 1)),
          slot: selectedSlot,
        })
      }
      setData(result || null)
      onDataLoaded?.(result, selectedDate)
    } catch (error) {
      console.error('Error fetching activity:', error)
      setData(null)
      onDataLoaded?.(null, selectedDate)
    } finally {
      setFetchingActivity(false)
    }
  }



  const suggestedDriver = React.useMemo(() => {
    if (suggestedDriverId == null) return null
    return drivers?.find((d) => d.id === suggestedDriverId) || null
  }, [suggestedDriverId, drivers])

  const suggestedDriverName = suggestedDriver
    ? suggestedDriver.fullName || formatDriverName(suggestedDriver)
    : null

  const applyDriverSuggestion = React.useCallback(() => {
    if (!suggestedDriver) return
    setActiveSourceId('drivers')
    setSelectedVehicle(null)
    setSelectedDriver({
      id: suggestedDriver.id,
      name: suggestedDriverName,
      customId: suggestedDriver.customId,
    })
  }, [suggestedDriver, suggestedDriverName])


  React.useEffect(() => {
    onDateChange?.(selectedDate)
  }, [selectedDate])

  useEffect(() => {
    if (selectedDate && selectedEntity) {
      getDailyActivity()
    } else {
      setData(null)
    }
  }, [selectedDriver, selectedVehicle, selectedDate, selectedSlot])

  const handleActivityHoverFromChart = (activity: any) => {
    setHoveredActivity(activity)
  }
  const filteredActivities = activities.filter((activity) => {
    const dayStart = formatedDate.clone()
    const dayEnd = formatedDate.clone().add(1, 'day')
    const activityEnd = activity.endTime
      ? moment(activity.endTime)
      : moment(formatedDate).endOf('day')

    return (
      moment(activity.startTime).isBefore(dayEnd) &&
      activityEnd.isAfter(dayStart)
    )
  })

  React.useEffect(() => {
    if (!wrapperRef.current) return

    const updateWidth = () => {
      if (wrapperRef.current) {
        setChartWidth(wrapperRef.current.offsetWidth)
      }
    }

    updateWidth()

    const resizeObserver = new ResizeObserver(updateWidth)
    resizeObserver.observe(wrapperRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [data, currentDate])
  const goToDate = (days: number) => {
    const newDate = moment(selectedDate || currentDate).add(days, 'days')
    setSelectedDate(newDate.format('YYYY-MM-DD'))
  }
  useEffect(() => {
    if (passedDays !== 0) {
      goToDate(passedDays)
      setPassedDays(0)
    }
  }, [passedDays])
  return (
    <div className="tw-relative tw-flex tw-flex-1 tw-flex-col tw-gap-6 tw-px-2">
      <div className="tw-relative tw-flex tw-w-full tw-gap-2">
        <div className="tw-w-80">
          <FieldsetMultiSourceSelect
            background
            className="tw-w-full"
            // label="Wybierz kierowcę lub pojazd"
            placeholder={isLoading ? t('common.loading') : t('common.select')}
            value={selectedEntity?.id?.toString() || ''}
            name="driver"
            disabled={isDisabled}
            onSourceChange={(sourceId) => {
              setActiveSourceId(sourceId)
              setSelectedDriver(null)
              setSelectedVehicle(null)
            }}
            onChange={(e) => {
              const selectedId = e.target.value

              if (activeSourceId === 'drivers') {
                const driver = drivers.find(
                  (d) => d.id.toString() === selectedId,
                )
                if (driver) {
                  setSelectedVehicle(null)
                  setSelectedDriver({
                    id: driver.id,
                    name: driver.fullName || formatDriverName(driver),
                    customId: driver.customId,
                  })
                }
              } else if (activeSourceId === 'vehicles') {
                const vehicle = vehicles.find(
                  (v) => v.id.toString() === selectedId,
                )
                if (vehicle) {
                  setSelectedDriver(null)
                  setSelectedVehicle({
                    id: vehicle.id,
                    name: vehicle.registrationNumber,
                    registrationNumber: vehicle.registrationNumber,
                  })
                }
              }
            }}
            sources={[
              {
                id: 'drivers',
                label: 'Kierowcy',
                options: drivers?.length
                  ? drivers.map((driver) => ({
                      label: driver.fullName || formatDriverName(driver),
                      value: driver.id.toString(),
                    }))
                  : [
                      {
                        label: t('driverModule.no-drivers'),
                        value: '',
                        sublabel: '',
                      },
                    ],
              },
              {
                id: 'vehicles',
                label: 'Pojazdy',
                options: vehicles?.length
                  ? vehicles.map((vehicle) => ({
                      label:
                        vehicle.registrationNumber || `Pojazd ${vehicle.id}`,
                      value: vehicle.id.toString(),
                    }))
                  : [
                      {
                        label: t('driverModule.no-vehicles'),
                        value: '',
                        sublabel: '',
                      },
                    ],
              },
            ]}
          />
        </div>
        <div className="tw-max-w-48">
          <DateField
            // label="Data"
            value={selectedDate}
            setValue={(e) => setSelectedDate(e)}
            disabled={isDisabled}
          />
        </div>
        {selectedVehicle && (
          <div className="tw-flex tw-items-end tw-gap-2">
            <div
              className={`tw-mb-[1px] tw-grid tw-h-[35.5px] tw-cursor-pointer tw-place-items-center tw-rounded-md tw-px-1.5 ${selectedSlot === '1' ? 'tw-bg-primary' : 'tw-bg-white'}`}
              onClick={() => setSelectedSlot('1')}
            >
              <FaIcon
                icon={fa1}
                className={`${selectedSlot === `1` ? 'tw-text-white' : ' '}`}
              />
            </div>
            <div
              className={`tw-mb-[1px] tw-grid tw-h-[35.5px] tw-cursor-pointer tw-place-items-center tw-rounded-md tw-px-1.5 ${selectedSlot === '2' ? 'tw-bg-primary' : 'tw-bg-white'}`}
              onClick={() => setSelectedSlot('2')}
            >
              <FaIcon
                icon={fa2}
                className={`${selectedSlot === `2` ? 'tw-text-white' : ' '}`}
              />
            </div>
          </div>
        )}
        <div className="tw-flex tw-items-center tw-gap-2 tw-pl-2">
          <Tooltip text={t('compareNavigation.previousThreeDays')} asChild>
            <button onClick={() => goToDate(-3)}>
              <img src={ChevronsTripleLeft} />
            </button>
          </Tooltip>
          <DividerVertical />
          <Tooltip text={t('compareNavigation.previousTwoDays')} asChild>
            <button onClick={() => goToDate(-2)}>
              <FaIcon icon={faChevronsLeftSolid} />
            </button>
          </Tooltip>
          <DividerVertical />
          <Tooltip text={t('compareNavigation.previousDay')} asChild>
            <button onClick={() => goToDate(-1)}>
              <FaIcon icon={faChevronLeft} />
            </button>
          </Tooltip>
          <DividerVertical />
          <Tooltip text={t('compareNavigation.nextDay')} asChild>
            <button onClick={() => goToDate(1)}>
              <FaIcon icon={faChevronRight} />
            </button>
          </Tooltip>
          <DividerVertical />
          <Tooltip text={t('compareNavigation.nextTwoDays')} asChild>
            <button onClick={() => goToDate(2)}>
              <FaIcon icon={faChevronsRightSolid} />
            </button>
          </Tooltip>
          <DividerVertical />
          <Tooltip text={t('compareNavigation.nextThreeDays')} asChild>
            <button onClick={() => goToDate(3)}>
              <img src={ChevronsTripleLeft} className="tw-rotate-180" />
            </button>
          </Tooltip>
        {suggestedDate && suggestedDate !== selectedDate && (

          <button
            type="button"
            onClick={() => setSelectedDate(suggestedDate)}
            disabled={isDisabled}
            className="tw-mb-[1px] tw-ml-auto tw-flex tw-h-[35.5px] tw-items-center tw-gap-1 tw-self-end tw-rounded-md tw-border tw-border-primary tw-bg-white tw-px-2 tw-text-xs tw-font-medium tw-text-primary hover:tw-bg-secondary disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
            title={t('common.select')}
          >
            <span>{t('common.select')}</span>
            <span className="tw-font-semibold">{suggestedDate}</span>
          </button>
        )}
        {suggestedDriver &&
          (!selectedDriver || selectedDriver.id !== suggestedDriver.id) && (
          
            <button
              type="button"
              onClick={applyDriverSuggestion}
              disabled={isDisabled}
              className={`tw-mb-[1px] tw-flex tw-h-[35.5px] tw-items-center tw-gap-1 tw-self-end tw-rounded-md tw-border tw-border-primary tw-bg-white tw-px-2 tw-text-xs tw-font-medium tw-text-primary hover:tw-bg-secondary disabled:tw-cursor-not-allowed disabled:tw-opacity-50 ${
                suggestedDate && suggestedDate !== selectedDate
                  ? ''
                  : 'tw-ml-auto'
              }`}
              title={t('common.select')}
            >
              <span>{t('common.select')}</span>
              <span className="tw-font-semibold">{suggestedDriverName}</span>
            </button>
          )}
        </div>
      </div>
      <div ref={wrapperRef} className="tw-relative tw-flex tw-flex-col">
        {!isLoadingData &&
          (!selectedDate ||
            !selectedEntity ||
            filteredActivities.length === 0) && <NoDataAvailable />}
        {isLoadingData ? (
          <LoadingState />
        ) : (
          <Timeline
            key={`compare-${formatedDate.toISOString()}`}
            {...({
              startTime: formatedDate,
              days: 1,
              width: chartWidth,
              height: SCENE_HEIGHT,
              extraCurrentDate: currentDate,
              translate: t,
              activities: filteredActivities,
              places: data?.places || [],
              cardInserts: data?.cardInserts || [],
              dayActivities: data?.dayActivities || [],
              violations: data?.violations || [],
              dailyRests: data?.dailyRests || [],
              dailyPartialRegularRests: data?.dailyPartialRegularRests || [],
              weeklyRests: data?.weeklyRests || [],
              weeklyReducedRests: data?.weeklyReducedRests || [],
              breaks: data?.breaks || [],
              partialBreaks: data?.partialBreaks || [],
              weekRecompenses: data?.weekRecompenses || [],
              existingDelegations:
                (data?.existingDelegations as ExistingDelegationModel[]) || [],
              outOfScopes: data?.outOfScopes || [],
              changeAreas: [],
              tmsData: [],
              minimalPaymentCountries: [],
              periodSelections: [],
              delegationMarkers: [],
              allEvents: [],
              allTripEvents: data?.tripEvents || [],
              allPreviousTripEvents: [],
              countries: [],
              canEdit: true,
              canGenerateAttestation: false,
              selectedActivity: hoveredActivity,
              editedActivity: null,
              isAddingNew: false,
              flattenChart: false,
              simpleMode: false,
              disableTooltips: false,
              showViolations: true,
              showTrips: true,
              showRests: true,
              showRestParts: true,
              showDayActivities: true,
              showDelegations: true,
              editMode: false,
              onSelected: handleActivityHoverFromChart,
              onContextMenuChanged: () => {},
              onViolationSelected: () => {},
              onPeriodSelectionsChanged: () => {},
            } as unknown as TimelineProps)}
          />
        )}
      </div>
    </div>
  )
}

export default CompareChart

const LoadingState = () => (
  <div className="tw-top-12 tw-z-50 tw-flex tw-h-[100px] tw-w-full tw-items-center tw-justify-center">
    <LoaderIcon className="tw-w-16 tw-animate-spin" />
  </div>
)
const NoDataAvailable = () => {
  const { t } = useLocalize()
  return (
    <div className="tw-absolute tw-top-4 tw-z-50 tw-w-full tw-text-center tw-text-gray-500">
      {t('driverModule.select-driver-vehicle-date')}
    </div>
  )
}
